/**
 * AdminPortalHomeLayout — the bandless chrome for the admin launch-portal
 * HOME at `/admin` (025-admin-area, FR-009/FR-011).
 *
 * Unlike its sibling `AdminPortalLayout` (the slim "Ylläpito" band shared by
 * the portal-styled SECTIONS, e.g. `/admin/feedback`), the portal HOME is a
 * clean full-screen launch springboard with NO top band: a centered welcome
 * over the 2x2 section grid on an otherwise empty surface. This mirrors the
 * proto, whose admin landing is a chrome-less centered screen — the band only
 * appears once a section is entered.
 *
 * The layout therefore carries no logo, no divider, and no page-title `<h1>`.
 * The rendered page (`AdminPortal`) owns the single page-level `<h1>`
 * ("Tervetuloa!"), so the heading-order contract still holds: one `<h1>` per
 * page, and here that `<h1>` is the portal's own welcome heading (not an
 * `<h2>` sitting under a band's `<h1>`, as it was when the home shared
 * `AdminPortalLayout`).
 *
 * A11y is preserved without the band:
 *
 *   - `<SkipLink />` first, so keyboard users still get the canonical "Siirry
 *     pääsisältöön" affordance the moment they Tab in (FR-005).
 *   - `<main id="main-content" tabIndex={-1}>` is the focusable landmark the
 *     SkipLink hash-jumps to — identical to the other shells.
 *
 * The springboard centering itself lives in `AdminPortal` (mirroring the
 * proto, where the centering is on the dashboard, not its frame); this layout
 * just provides the full-height flex column + a `flex-1` main so the page can
 * center within the viewport.
 *
 * ## Token-only styling
 *
 * Layout utilities + the `bg-background` token only. No raw hex, no `*-[…]`
 * bracket utilities (FR-047 / no-token-bypass).
 */

import type { JSX, ReactNode } from 'react';

import { SkipLink } from './SkipLink.js';

const shellClasses = 'flex min-h-screen flex-col bg-background';
// Main fills the shell so `AdminPortal` (a `flex-1` child) can center the
// welcome + grid on both axes within the viewport.
const mainClasses = 'flex flex-1 flex-col';

export interface AdminPortalHomeLayoutProps {
  /** Routed content rendered inside the `<main>` landmark (the portal home). */
  readonly children?: ReactNode;
}

export function AdminPortalHomeLayout({ children }: AdminPortalHomeLayoutProps): JSX.Element {
  return (
    <div className={shellClasses}>
      <SkipLink />
      <main id="main-content" tabIndex={-1} className={mainClasses}>
        {children}
      </main>
    </div>
  );
}
